import { useMemo, useState } from "react";
import "@fontsource-variable/bodoni-moda";

import type { SiteProps, Slot } from "../types";
import "../styles/homes.css";

interface HomeAddress {
  id: string;
  label: string;
  lot: string;
  quarter: string;
  model: string;
}

const HOME_ADDRESSES: HomeAddress[] = [
  {
    id: "apricot-mews-01",
    label: "01 Apricot Mews",
    lot: "LOT 01",
    quarter: "Marmalade Quarter",
    model: "The Optimist",
  },
  {
    id: "satellite-close-07",
    label: "07 Satellite Close",
    lot: "LOT 07",
    quarter: "North Orbit",
    model: "The Moonshot",
  },
  {
    id: "cache-lane-13",
    label: "13 Cache Lane",
    lot: "LOT 13",
    quarter: "Memory Gardens",
    model: "The Keepsake",
  },
  {
    id: "browser-terrace-22",
    label: "22 Browser Terrace",
    lot: "LOT 22",
    quarter: "Window District",
    model: "The Tab House",
  },
  {
    id: "loading-lane-42",
    label: "42 Loading Lane",
    lot: "LOT 42",
    quarter: "The Waiting Room",
    model: "The Spinner",
  },
  {
    id: "cookie-crescent-64",
    label: "64 Cookie Crescent",
    lot: "LOT 64",
    quarter: "Consent Gardens",
    model: "The Accept All",
  },
  {
    id: "hyperlink-heights-88",
    label: "88 Hyperlink Heights",
    lot: "LOT 88",
    quarter: "Blue Text Hill",
    model: "The Underline",
  },
  {
    id: "not-found-road-404",
    label: "404 Not Found Road",
    lot: "LOT 404",
    quarter: "Outer Internet",
    model: "The Missing Semi",
  },
];

const wholeUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const centsUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function money(amountCents: number): string {
  return (amountCents % 100 === 0 ? wholeUsd : centsUsd).format(amountCents / 100);
}

function displayDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0] || url;
  }
}

function orderedEntries(slot: Slot) {
  return [...slot.entries].sort(
    (a, b) => a.rank - b.rank || b.amountCents - a.amountCents,
  );
}

interface HomeLotProps {
  address: HomeAddress;
  slot: Slot;
  selected: boolean;
  minimumBidCents: number;
  onSelect: (id: string) => void;
}

function HomeLot({
  address,
  slot,
  selected,
  minimumBidCents,
  onSelect,
}: HomeLotProps) {
  const leader = slot.entries[0];

  return (
    <button
      className={`homes-lot${selected ? " homes-lot--selected" : ""}`}
      type="button"
      aria-pressed={selected}
      aria-controls="homes-property-register"
      aria-label={`${address.label}. ${
        leader
          ? `${leader.name} currently leads at ${money(leader.amountCents)}.`
          : `Available from ${money(minimumBidCents)}.`
      } Select this address.`}
      onClick={() => onSelect(address.id)}
    >
      <span className="homes-lot__mark" aria-hidden="true">
        <i />
      </span>
      <span className="homes-lot__copy">
        <small>{address.lot}</small>
        <strong>{address.label}</strong>
        <span>{leader ? `${leader.name} is #1` : "Available"}</span>
      </span>
      <b>{leader ? money(leader.amountCents) : money(minimumBidCents)}</b>
    </button>
  );
}

export default function HomesSite({ slots, state, onClaim }: SiteProps) {
  const [selectedAddressId, setSelectedAddressId] = useState(
    HOME_ADDRESSES[0].id,
  );

  const properties = useMemo(() => {
    const paidSlots = new Map(slots.map((slot) => [slot.id, slot]));

    return HOME_ADDRESSES.map((address) => {
      const paidSlot = paidSlots.get(address.id);
      const slot: Slot = paidSlot
        ? {
            ...paidSlot,
            label: address.label,
            entries: orderedEntries(paidSlot),
          }
        : { id: address.id, label: address.label, entries: [] };

      return { address, slot };
    });
  }, [slots]);

  const selectedProperty =
    properties.find(({ address }) => address.id === selectedAddressId) ??
    properties[0];
  const selectedEntries = selectedProperty.slot.entries;
  const selectedLeader = selectedEntries[0];
  const nextWinningTotal = Math.max(
    state.minimumBidCents,
    selectedLeader ? Math.ceil((selectedLeader.amountCents + 1) / 100) * 100 : 0,
  );
  const occupiedHomes = properties.filter(
    ({ slot }) => slot.entries.length > 0,
  ).length;

  function selectAddress(id: string): void {
    setSelectedAddressId(id);
    if (window.matchMedia("(max-width: 900px)").matches) {
      window.requestAnimationFrame(() => {
        document.getElementById("homes-property-register")?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    }
  }

  return (
    <main className="homes-site">
      <header className="homes-header">
        <a className="homes-wordmark" href="#homes-top" aria-label="Outbid Homes home">
          OUTBID<span>.HOMES</span>
        </a>

        <p className="homes-header__status">
          <i aria-hidden="true" /> {occupiedHomes > 0 ? `${occupiedHomes} of ${HOME_ADDRESSES.length} occupied` : `${HOME_ADDRESSES.length} addresses open`}
        </p>

        <nav className="homes-nav" aria-label="Homes page navigation">
          <a href="#homes-neighborhood">Addresses</a>
          <a href="/rules">Rules</a>
        </nav>
      </header>

      <section className="homes-hero" id="homes-top" aria-labelledby="homes-title">
        <div className="homes-hero__copy">
          <p className="homes-kicker">DIGITAL PROPERTY OFFICE · {state.stripeEnabled ? "BIDDING OPEN" : "VIEWING ONLY"}</p>
          <h1 id="homes-title">Put your URL on the lawn.</h1>
          <p className="homes-intro">
            Choose an address. The highest paid total gets its front-garden sign;
            every other bidder stays in the public register.
          </p>
        </div>

        <aside className="homes-quick-claim" aria-label="Quick claim">
          <div className="homes-quick-claim__top">
            <span>{selectedLeader ? "CURRENT #1" : "AVAILABLE NOW"}</span>
            <b>{selectedProperty.address.lot}</b>
          </div>
          <strong>{selectedProperty.address.label}</strong>
          <p>
            {!state.stripeEnabled
              ? `Explore the register. Checkout is not connected yet.`
              : selectedLeader
              ? `Beat ${money(selectedLeader.amountCents)} to take the sign.`
              : `Be first on the sign from ${money(state.minimumBidCents)}.`}
          </p>
          <button type="button" disabled={!state.stripeEnabled} onClick={() => onClaim(selectedProperty.slot)}>
            {state.stripeEnabled ? "Enter this ranking" : "Payments opening soon"}
            <span>{state.stripeEnabled ? `from ${money(state.minimumBidCents)} →` : "VIEW ONLY"}</span>
          </button>
        </aside>
      </section>

      <section
        className="homes-market"
        id="homes-neighborhood"
        aria-labelledby="homes-market-title"
      >
        <header className="homes-market__heading">
          <div>
            <p>THE DEVELOPMENT</p>
            <h2 id="homes-market-title">Choose your address</h2>
          </div>
          <p>
            {state.paidBidCount > 0
              ? `${state.paidBidCount} paid ${state.paidBidCount === 1 ? "bid" : "bids"} · ${money(state.grossCents)} recorded`
              : `First listing starts at ${money(state.minimumBidCents)}`}
          </p>
        </header>

        <div className="homes-workbench">
          <div className="homes-addresses" aria-label="Available digital addresses">
            {properties.map(({ address, slot }) => (
              <HomeLot
                address={address}
                slot={slot}
                selected={address.id === selectedProperty.address.id}
                minimumBidCents={state.minimumBidCents}
                onSelect={selectAddress}
                key={address.id}
              />
            ))}
          </div>

          <aside
            className="homes-register"
            id="homes-property-register"
            aria-labelledby="homes-register-title"
          >
            <header className="homes-register__header" aria-live="polite">
              <div>
                <p>{selectedProperty.address.quarter}</p>
                <h2 id="homes-register-title">{selectedProperty.address.label}</h2>
                <span>{selectedProperty.address.model}</span>
              </div>
              <b>{selectedLeader ? `${selectedEntries.length} ranked` : "Vacant"}</b>
            </header>

            <section className="homes-resident-card" aria-label="Current leading bidder">
              <span>FRONT-GARDEN SIGN</span>
              {selectedLeader ? (
                <>
                  <a href={selectedLeader.url} target="_blank" rel="noreferrer">
                    {selectedLeader.name} <span aria-hidden="true">↗</span>
                  </a>
                  <p>{selectedLeader.tagline}</p>
                  <small>{displayDomain(selectedLeader.url)}</small>
                </>
              ) : (
                <>
                  <strong>Your URL could live here.</strong>
                  <p>No paid bids on this address yet.</p>
                </>
              )}
            </section>

            <div className="homes-offer">
              <span>
                <small>{selectedLeader ? "TOTAL TO TAKE #1" : "OPENING BID"}</small>
                <strong>{money(nextWinningTotal)}</strong>
              </span>
              <button type="button" disabled={!state.stripeEnabled} onClick={() => onClaim(selectedProperty.slot)}>
                {state.stripeEnabled ? "Choose a bid" : "Payments opening soon"} →
              </button>
            </div>

            <div className="homes-ranking-heading">
              <h3>Public bid register</h3>
              <span>Everyone stays listed</span>
            </div>

            <ol className="homes-ranking">
              {selectedEntries.length ? (
                selectedEntries.map((entry) => (
                  <li key={entry.id}>
                    <span>#{entry.rank}</span>
                    <div>
                      <strong>{entry.name}</strong>
                      <a href={entry.url} target="_blank" rel="noreferrer">
                        {displayDomain(entry.url)} ↗
                      </a>
                    </div>
                    <b>{money(entry.amountCents)}</b>
                  </li>
                ))
              ) : (
                <li className="homes-ranking__empty">
                  No bidders yet. The first paid bid becomes #1.
                </li>
              )}
            </ol>

            <p className="homes-register__note">
              Outbid bidders move down, not out. Ties use payment time, then
              Checkout Session ID.
            </p>
          </aside>
        </div>
      </section>

      <footer className="homes-footer">
        <strong>OUTBID.HOMES</strong>
        <p>A tiny neighborhood for internet people.</p>
        <nav aria-label="Legal links">
          <a href="/rules">Rules</a>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
        </nav>
      </footer>
    </main>
  );
}
